import { useEffect, useRef, useState } from "react";
import {
  Cloud,
  Edit2,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadThumbnailToCOS, uploadToCOS } from "@/lib/cos-upload";
import { normalizeCategoryList, getAllKnownCategoryOptions } from "@/lib/category-config";
import { addCustomCategoryOption, useCategoryOptions } from "@/hooks/useCategoryOptions";

interface VideoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: {
    id: string;
    title: string;
    description: string | null;
    video_url: string;
    thumbnail_url: string | null;
    jlpt_level: "N5" | "N4" | "N3" | "N2" | "N1";
    jlpt_levels?: string[];
    category: string;
    categories?: string[];
    is_published: boolean;
    is_premium: boolean;
    duration: number;
    series_id?: string;
  };
  onSuccess: () => void;
}

interface SubtitleRow {
  id: string;
  start_time: number;
  end_time: number;
  japanese: string;
  chinese: string;
  romaji: string | null;
  sort_order: number | null;
  isEditing?: boolean;
  isNew?: boolean;
}

const JLPT_LEVELS = ["N5", "N4", "N3", "N2", "N1"] as const;

type JlptLevel = (typeof JLPT_LEVELS)[number];

function formatSecondsToClock(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return `${mins}:${secs.padStart(4, "0")}`;
}

function parseClockToSeconds(value: string): number {
  const parts = value.split(":");
  if (parts.length !== 2) return Number(value) || 0;
  return Number(parts[0] || 0) * 60 + Number(parts[1] || 0);
}

export default function VideoEditDialog({
  open,
  onOpenChange,
  video,
  onSuccess,
}: VideoEditDialogProps) {
  const { toast } = useToast();

  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<"basic" | "media" | "subtitles">("basic");
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const [seriesList, setSeriesList] = useState<Array<{ id: string; title: string }>>([]);

  const { data: categoryOptions = getAllKnownCategoryOptions(), refetch: refetchCategoryOptions } =
    useCategoryOptions();

  const [formData, setFormData] = useState({
    title: video.title,
    description: video.description || "",
    jlpt_level: video.jlpt_level,
    jlpt_levels: Array.isArray(video.jlpt_levels) && video.jlpt_levels.length > 0
      ? video.jlpt_levels
      : [video.jlpt_level],
    category: video.category,
    categories: Array.isArray(video.categories) && video.categories.length > 0
      ? video.categories
      : [video.category],
    series_id: video.series_id || "none",
    is_published: video.is_published,
    is_premium: video.is_premium,
    thumbnail_url: video.thumbnail_url || "",
    video_url: video.video_url,
  });

  const [subtitles, setSubtitles] = useState<SubtitleRow[]>([]);
  const [loadingSubtitles, setLoadingSubtitles] = useState(false);
  const [editingSubtitle, setEditingSubtitle] = useState<SubtitleRow | null>(null);

  useEffect(() => {
    setFormData({
      title: video.title,
      description: video.description || "",
      jlpt_level: video.jlpt_level,
      jlpt_levels: Array.isArray(video.jlpt_levels) && video.jlpt_levels.length > 0
        ? video.jlpt_levels
        : [video.jlpt_level],
      category: video.category,
      categories: Array.isArray(video.categories) && video.categories.length > 0
        ? video.categories
        : [video.category],
      series_id: video.series_id || "none",
      is_published: video.is_published,
      is_premium: video.is_premium,
      thumbnail_url: video.thumbnail_url || "",
      video_url: video.video_url,
    });
    setActiveTab("basic");
    setSubtitles([]);
    setEditingSubtitle(null);
  }, [video]);

  useEffect(() => {
    if (!open) return;

    const fetchSeries = async () => {
      const { data } = await supabase.from("series").select("id,title").order("title");
      setSeriesList(data || []);
    };

    fetchSeries();
  }, [open]);

  useEffect(() => {
    if (activeTab !== "subtitles" || subtitles.length > 0) return;

    const loadSubtitles = async () => {
      setLoadingSubtitles(true);
      try {
        const { data, error } = await supabase
          .from("subtitles")
          .select("*")
          .eq("video_id", video.id)
          .order("sort_order", { ascending: true });

        if (error) throw error;
        setSubtitles((data || []) as SubtitleRow[]);
      } catch (error) {
        console.error("Failed to load subtitles", error);
        toast({ title: "Failed to load subtitles", variant: "destructive" });
      } finally {
        setLoadingSubtitles(false);
      }
    };

    loadSubtitles();
  }, [activeTab, subtitles.length, toast, video.id]);

  const createCategory = (input: string) => {
    const created = addCustomCategoryOption(input);
    if (!created) return null;

    void refetchCategoryOptions();
    toast({
      title: "Category created",
      description: `${created.label} is now selectable`,
    });
    return created;
  };

  const saveBasic = async () => {
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }

    const categories = normalizeCategoryList(formData.categories);
    const category = categories[0] || "conversation";
    const levels = Array.from(new Set(formData.jlpt_levels.filter(Boolean)));
    const jlpt = (levels[0] as JlptLevel) || "N5";

    setSaving(true);
    try {
      const { error } = await supabase
        .from("videos")
        .update({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          jlpt_level: jlpt,
          jlpt_levels: levels.length > 0 ? levels : [jlpt],
          category,
          categories,
          series_id: formData.series_id === "none" ? null : formData.series_id,
          is_published: formData.is_published,
          is_premium: formData.is_premium,
          thumbnail_url: formData.thumbnail_url || null,
          video_url: formData.video_url,
        })
        .eq("id", video.id);

      if (error) throw error;

      toast({ title: "Saved" });
      onSuccess();
    } catch (error) {
      console.error("Failed to save video", error);
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadThumbnail = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image", variant: "destructive" });
      return;
    }

    setUploadingThumbnail(true);
    try {
      const result = await uploadThumbnailToCOS(file);
      if (!result.success || !result.url) {
        throw new Error(result.error || "Upload failed");
      }

      setFormData((prev) => ({ ...prev, thumbnail_url: result.url! }));
      toast({ title: "Thumbnail updated" });
    } catch (error) {
      console.error("Failed to upload thumbnail", error);
      toast({ title: "Thumbnail upload failed", variant: "destructive" });
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleUploadVideo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      toast({ title: "Please select a video", variant: "destructive" });
      return;
    }

    setUploadingVideo(true);
    try {
      const result = await uploadToCOS({ file, folder: "videos" });
      if (!result.success || !result.url) {
        throw new Error(result.error || "Upload failed");
      }

      setFormData((prev) => ({ ...prev, video_url: result.url! }));
      toast({ title: "Video file updated" });
    } catch (error) {
      console.error("Failed to upload video", error);
      toast({ title: "Video upload failed", variant: "destructive" });
    } finally {
      setUploadingVideo(false);
    }
  };

  const addSubtitle = () => {
    const last = subtitles[subtitles.length - 1];
    const start = last?.end_time || 0;
    const next: SubtitleRow = {
      id: `tmp-${Date.now()}`,
      start_time: start,
      end_time: start + 3,
      japanese: "",
      chinese: "",
      romaji: null,
      sort_order: subtitles.length,
      isEditing: true,
      isNew: true,
    };

    setSubtitles((prev) => [...prev, next]);
    setEditingSubtitle(next);
  };

  const removeSubtitle = async (id: string) => {
    try {
      const { error } = await supabase.from("subtitles").delete().eq("id", id);
      if (error) throw error;

      setSubtitles((prev) => prev.filter((item) => item.id !== id));
      toast({ title: "Subtitle removed" });
    } catch (error) {
      console.error("Failed to remove subtitle", error);
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const saveSubtitle = async (subtitle: SubtitleRow) => {
    try {
      if (subtitle.isNew) {
        const { data, error } = await supabase
          .from("subtitles")
          .insert({
            video_id: video.id,
            start_time: subtitle.start_time,
            end_time: subtitle.end_time,
            japanese: subtitle.japanese,
            chinese: subtitle.chinese,
            romaji: subtitle.romaji,
            sort_order: subtitle.sort_order ?? subtitles.length,
          })
          .select()
          .single();

        if (error || !data) throw error || new Error("Insert failed");

        setSubtitles((prev) =>
          prev.map((item) =>
            item.id === subtitle.id ? ({ ...(data as SubtitleRow), isEditing: false } as SubtitleRow) : item,
          ),
        );
      } else {
        const { error } = await supabase
          .from("subtitles")
          .update({
            start_time: subtitle.start_time,
            end_time: subtitle.end_time,
            japanese: subtitle.japanese,
            chinese: subtitle.chinese,
            romaji: subtitle.romaji,
          })
          .eq("id", subtitle.id);

        if (error) throw error;

        setSubtitles((prev) =>
          prev.map((item) =>
            item.id === subtitle.id ? ({ ...subtitle, isEditing: false } as SubtitleRow) : item,
          ),
        );
      }

      setEditingSubtitle(null);
      toast({ title: "Subtitle saved" });
    } catch (error) {
      console.error("Failed to save subtitle", error);
      toast({ title: "Save subtitle failed", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit Video</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="flex flex-1 flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="subtitles">Subtitles</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 overflow-y-auto px-1">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>JLPT Levels</Label>
                <MultiSelect
                  options={JLPT_LEVELS.map((level) => ({ value: level, label: level }))}
                  selected={formData.jlpt_levels}
                  onChange={(selected) => {
                    const levels = Array.from(new Set(selected.filter(Boolean)));
                    setFormData((prev) => ({
                      ...prev,
                      jlpt_levels: levels,
                      jlpt_level: (levels[0] as JlptLevel) || "N5",
                    }));
                  }}
                  placeholder="Select levels"
                />
              </div>

              <div className="space-y-2">
                <Label>Categories</Label>
                <MultiSelect
                  options={categoryOptions}
                  selected={formData.categories}
                  onChange={(selected) => {
                    const categories = normalizeCategoryList(selected);
                    setFormData((prev) => ({
                      ...prev,
                      categories,
                      category: categories[0] || "conversation",
                    }));
                  }}
                  allowCreate
                  onCreateOption={createCategory}
                  createText="Create"
                  searchPlaceholder="Search or create"
                  placeholder="Select categories"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Series</Label>
              <Select
                value={formData.series_id}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, series_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No series" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No series</SelectItem>
                  {seriesList.map((series) => (
                    <SelectItem key={series.id} value={series.id}>
                      {series.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">Published</p>
                  <p className="text-sm text-muted-foreground">Visible to users</p>
                </div>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_published: checked,
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">Premium</p>
                  <p className="text-sm text-muted-foreground">Member only content</p>
                </div>
                <Switch
                  checked={formData.is_premium}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_premium: checked,
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end pb-2">
              <Button onClick={saveBasic} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-5 overflow-y-auto px-1">
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Thumbnail
              </Label>
              <div className="flex items-start gap-4">
                {formData.thumbnail_url ? (
                  <img
                    src={formData.thumbnail_url}
                    alt="thumbnail"
                    className="h-24 w-40 rounded-lg border object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-40 items-center justify-center rounded-lg border bg-muted">
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}

                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                  >
                    {uploadingThumbnail ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Replace thumbnail
                  </Button>

                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleUploadThumbnail}
                  />

                  <p className="text-xs text-muted-foreground">JPG/PNG, uploaded to COS</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Cloud className="h-4 w-4" />
                Video File
              </Label>

              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{formData.video_url}</p>
                    <p className="text-xs text-muted-foreground">
                      Duration: {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, "0")}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                  >
                    {uploadingVideo ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Re-upload
                  </Button>

                  <input
                    ref={videoInputRef}
                    type="file"
                    className="hidden"
                    accept="video/*"
                    onChange={handleUploadVideo}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pb-2">
              <Button onClick={saveBasic} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="subtitles" className="flex flex-1 flex-col overflow-hidden px-1">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{subtitles.length} subtitles</p>
              <Button size="sm" onClick={addSubtitle}>
                <Plus className="mr-1 h-4 w-4" />
                Add line
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto rounded-lg border">
              {loadingSubtitles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : subtitles.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No subtitles</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">Time</TableHead>
                      <TableHead>Japanese</TableHead>
                      <TableHead>Chinese</TableHead>
                      <TableHead>Romaji</TableHead>
                      <TableHead className="w-24 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subtitles.map((subtitle) => {
                      const isEditing = editingSubtitle?.id === subtitle.id;

                      return (
                        <TableRow key={subtitle.id}>
                          <TableCell className="text-xs font-mono text-muted-foreground">
                            {isEditing ? (
                              <div className="space-y-1">
                                <Input
                                  className="h-7 text-xs"
                                  value={formatSecondsToClock(editingSubtitle.start_time)}
                                  onChange={(event) =>
                                    setEditingSubtitle((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            start_time: parseClockToSeconds(event.target.value),
                                          }
                                        : prev,
                                    )
                                  }
                                />
                                <Input
                                  className="h-7 text-xs"
                                  value={formatSecondsToClock(editingSubtitle.end_time)}
                                  onChange={(event) =>
                                    setEditingSubtitle((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            end_time: parseClockToSeconds(event.target.value),
                                          }
                                        : prev,
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <>
                                {formatSecondsToClock(subtitle.start_time)}
                                <br />
                                {formatSecondsToClock(subtitle.end_time)}
                              </>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm"
                                value={editingSubtitle.japanese}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, japanese: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm">{subtitle.japanese}</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm"
                                value={editingSubtitle.chinese}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, chinese: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground">{subtitle.chinese}</span>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <Textarea
                                rows={2}
                                className="text-sm font-mono"
                                value={editingSubtitle.romaji || ""}
                                onChange={(event) =>
                                  setEditingSubtitle((prev) =>
                                    prev ? { ...prev, romaji: event.target.value } : prev,
                                  )
                                }
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground">{subtitle.romaji}</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            {isEditing ? (
                              <div className="flex justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => saveSubtitle(editingSubtitle)}
                                >
                                  <Save className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => {
                                    if (subtitle.isNew) {
                                      setSubtitles((prev) => prev.filter((item) => item.id !== subtitle.id));
                                    }
                                    setEditingSubtitle(null);
                                  }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setEditingSubtitle(subtitle)}
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => removeSubtitle(subtitle.id)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
